/**
 * VideoLesson component
 *
 * Description:
 * Manages the video lesson playback, tracking, and completion logic. Handles video/audio sources, captions, and interaction with Redux for lesson progress.
 *
 * Features:
 * - Handles video and audio playback, including captions.
 * - Tracks user progress and updates lesson status.
 * - Opens SCORM content in a new tab if required.
 * - Uses Material-UI for UI elements.
 */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getUserAditionalTrackCourse } from 'store/course-actions';

import {
  getStatusLessons,
  getTrackLoadLesson,
  setFinishLesson,
  setStartLesson,
  setVideoHistory
} from 'store/lesson-actions';

import { getLinkLtik } from 'constants/LTIData';

import VideoInfo from 'components/UI/VideoInfo/VideoInfo';
import classes from './VideoLesson.module.css';

const url = window.location.pathname.split('/');

const VideoLesson = props => {
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);
  const dataLesson = useSelector(state => state.lessonInfo);
  const playerRef = useRef(null);
  const transcriptParam = 30;
  let timeTranscript = 0;
  // Local state for audio player, captions, and loading
  const [audioPlayer, setAudioPlayer] = useState(false);
  const [hasCaption, setHasCaption] = useState(false);
  const [hasCaptionEn, setHasCaptionEn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (url.includes('aud')) {
      if (dataLesson.data.pathAudio) {
        setAudioPlayer(dataLesson.data.pathAudio);
      }
    }
    if (props.videoData.data.pathVtt) {
      let caption = props.videoData.data.pathVtt.split('/');
      if (caption[3] !== 'null') {
        setHasCaption(true);
      }
    }
    if (props.videoData.data.cc_en) {
      let captionEn = props.videoData.data.cc_en.split('/');
      if (captionEn[3] !== 'null') {
        setHasCaptionEn(true);
      }
    }
    setLoading(false);
    if (props.videoData.data.idRepo === 6) {
      window.open(
        `https://lesson.mayahii.com/${url[1]}/f/${dataAcademy.data.idAcademy}/${props.idCourse}/${props.idLesson}`,
        '_blank'
      );
    }
  }, []);

  // Video.js options for playback
  const videoJsOptions = {
    autoplay: !url.includes('lp') ? true : false,
    currentTime: 20,
    playbackRates: [0.5, 1, 1.5, 2],
    controls: true,
    responsive: true,
    fluid: true,
    sources: [
      {
        src:
          audioPlayer.length > 0
            ? audioPlayer
            : props.videoData.data.hlsMedium
            ? props.videoData.data.hlsMedium
            : props.videoData.data.idRepo === 2
            ? `https://mayahii-partners.s3.amazonaws.com/${props.videoData.data.claveVideo}`
            : `https://d3g2ltadvcvug.cloudfront.net/${props.videoData.data.claveVideo}`
      }
    ],
    tracks: [
      ...(props.videoData.data.pathVtt && hasCaption
        ? [
            {
              kind: 'captions',
              src: props.videoData.data.pathVtt,
              srclang: 'es',
              label: 'Español',
              default: true
            }
          ]
        : []),
      ...(props.videoData.data.cc_en && hasCaptionEn
        ? [
            {
              kind: 'captions',
              src: props.videoData.data.cc_en,
              srclang: 'en',
              label: 'Inglés'
            }
          ]
        : [])
    ]
  };

  /**
   * Handles player ready event, sets up tracking and transcript logic.
   */
  const handlePlayerReady = player => {
    const timeToSave = 5;
    const porcentToEnd = 0.9;
    let flagEnd = false;
    let auxTime = 0;
    playerRef.current = player;

    player.on('ready', () => {
      props.playerTimeHandler(props.videoData.videoHistory);
      timeTranscript = props.videoData.videoHistory;
      props.transcriptHandler(timeTranscript, transcriptParam);
      player.currentTime(props.videoData.videoHistory);
      auxTime = props.historyMedia;
      player.currentTime(props.historyMedia);
      dispatch(
        getTrackLoadLesson(
          props.idLesson,
          props.idCourse,
          dataAcademy.data.shortName
        )
      );
    });
    (async () => {
      await setStartLesson(
        props.idLesson,
        props.idCourse,
        dataAcademy.data.shortName,
        true
      );
    })();

    player.on('timeupdate', () => {
      let resetTime = false;
      let videoDuartion = props.videoData.data.duracion;
      let videoExtension = props.videoData.data.claveVideo;
      props.playerTimeHandler(resetTime ? 0 : player.currentTime());
      videoExtension = videoExtension.split('.');
      videoExtension = videoExtension[1];
      if (videoExtension !== 'mp4Mayahii1080p') {
        videoDuartion = videoDuartion / 1000;
      }
      if (player.currentTime() === 0 && !resetTime) {
        player.currentTime(auxTime);
      }
      if (Math.abs(auxTime - player.currentTime()) > timeToSave) {
        auxTime = player.currentTime();
        if (auxTime < timeTranscript) {
          props.transcriptHandler(auxTime, transcriptParam);
          timeTranscript = auxTime + transcriptParam;
        }
        if (auxTime > timeTranscript) {
          props.transcriptHandler(auxTime, transcriptParam);
          timeTranscript = auxTime + transcriptParam;
        }
        dispatch(
          setVideoHistory(
            props.idLesson,
            parseInt(auxTime),
            dataAcademy.data.shortName
          )
        );
        if (player.currentTime() !== 0) {
          dispatch(
            setVideoHistory(
              props.idLesson,
              parseInt(auxTime),
              dataAcademy.data.shortName
            )
          );
        }
        props.changeVideoStatus(1);
      }

      if (player.currentTime() === videoDuartion) {
        resetTime = true;
        auxTime = 0;
      } else {
        resetTime = false;
      }

      if (
        !flagEnd &&
        player.currentTime() / videoDuartion > porcentToEnd
      ) {
        flagEnd = true;
        (async () => {
          const finishLesson = await setFinishLesson(
            props.idLesson,
            props.idCourse,
            dataAcademy.data.shortName,
            true
          );
          if (finishLesson) {
            dispatch(getUserAditionalTrackCourse(props.idCourse));
            if (getLinkLtik()) {
              var gradeOptions = {
                method: 'POST',
                credentials: 'include',
                body: JSON.stringify({ debug: true }),
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + getLinkLtik()
                }
              };
              fetch(
                'https://lti13.mayahii.com/gradelti',
                gradeOptions
              )
                .then(response => response.json())
                .then(data => {
                  // Resultado de la llamda gradelti
                  console.log('data', data);
                });
            }
            dispatch(
              getStatusLessons(props.idCourse, props.idLesson)
            );
          }
        })();
      }
    });
  };

  return (
    !loading && (
      <>
        {props.videoData.data.idRepo !== 6 ? (
          <VideoInfo
            // Custom prop: 'options' passes video.js options
            options={videoJsOptions}
            // Custom prop: 'onReady' passes player ready handler
            onReady={handlePlayerReady}
            // Custom prop: 'audioPlayer' passes audio source
            audioPlayer={audioPlayer}
            // Custom prop: 'caption' passes caption file path
            caption={props.videoData.data.pathVtt}
            // Custom prop: 'hasCaption' passes caption flag
            hasCaption={hasCaption}
            // Custom prop: 'captionEn' passes English caption file path
            captionEn={props.videoData.data.cc_en}
            // Custom prop: 'hasCaptionEn' passes English caption flag
            hasCaptionEn={hasCaptionEn}
          />
        ) : (
          <div className={classes.scormContainer}>
            <p>El contenido se abrirá en una nueva pestaña</p>
          </div>
        )}
      </>
    )
  );
};

VideoLesson.propTypes = {
  /**
   * Video data object containing video and audio sources, captions, and metadata
   */
  videoData: PropTypes.object,
  /**
   * Course ID for lesson tracking
   */
  idCourse: PropTypes.number,
  /**
   * Lesson ID for lesson tracking
   */
  idLesson: PropTypes.number,
  /**
   * Function to change video status
   */
  changeVideoStatus: PropTypes.func,
  /**
   * Function to handle transcript updates
   */
  transcriptHandler: PropTypes.func,
  /**
   * Function to handle player time updates
   */
  playerTimeHandler: PropTypes.func,
  /**
   * Number representing last media history position
   */
  historyMedia: PropTypes.number
};

VideoLesson.defaultProps = {
  videoData: {},
  idCourse: undefined,
  idLesson: undefined,
  changeVideoStatus: undefined,
  transcriptHandler: undefined,
  playerTimeHandler: undefined,
  historyMedia: undefined
};

export default VideoLesson;
